import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaRobot } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import tripPlannerAPI from '../services/tripPlannerAPI';
import './TripPlanPage.css';

const TRIP_TYPES = [
  { id: 'family', label: 'Family' },
  { id: 'couple', label: 'Couple' },
  { id: 'solo', label: 'Solo' },
  { id: 'business', label: 'Business' },
  { id: 'group', label: 'Group' },
];

const TripPlanGeneratePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const generateState = location.state?.generateTrip;
  const [tripType, setTripType] = useState('couple');
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    if (!generateState) {
      navigate('/trips');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.info('Please sign in to generate your AI trip plan');
      navigate('/login', { state: { from: '/trip-plan/generate', generateTrip: generateState } });
      return;
    }

    setGenerating(true);
    try {
      const guests =
        typeof generateState.guests === 'number'
          ? { adults: generateState.guests, children: 0 }
          : generateState.guests || { adults: 1, children: 0 };

      const res = await tripPlannerAPI.generate({
        bookingId: generateState.bookingId || null,
        propertyId: generateState.property?._id || generateState.property?.id || null,
        destination: generateState.property?.city || '',
        checkIn: generateState.checkIn,
        checkOut: generateState.checkOut,
        guests,
        stayCost: generateState.totalPrice || 0,
        tripType,
        propertySnapshot: {
          propertyId: generateState.property?._id || generateState.property?.id,
          name: generateState.property?.name || '',
          city: generateState.property?.city || '',
          state: generateState.property?.state || '',
          image: generateState.property?.images?.[0] || generateState.property?.image || '',
        },
      });

      const newPlan = res.data?.plan;
      if (newPlan?._id) {
        toast.success('Your AI trip plan is ready!');
        navigate(`/trip-plan/${newPlan._id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate trip plan');
    } finally {
      setGenerating(false);
    }
  };

  if (!generateState) {
    return (
      <div className="trip-plan-page">
        <Navbar />
        <div className="trip-plan-container">
          <p>No booking data found. Book a stay first to generate a trip plan.</p>
          <button className="trip-plan-btn trip-plan-btn-primary" onClick={() => navigate('/search')}>
            Search Stays
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="trip-plan-page">
      <Navbar />
      <div className="trip-plan-container">
        {generating ? (
          <div className="trip-plan-loading">
            <div className="spinner" />
            <h2>UBSA AI is planning your trip...</h2>
            <p>Creating a personalized day-wise itinerary with attractions, dining & transport tips.</p>
          </div>
        ) : (
          <>
            <div className="trip-plan-hero">
              <div className="trip-plan-hero-badge">
                <FaRobot /> UBSA AI Trip Planner
              </div>
              <h1>Plan the Rest of Your Trip with UBSA AI</h1>
              <p>Don&apos;t just book your stay. Let UBSA AI plan your entire trip.</p>
            </div>

            <div className="trip-section">
              <h2>Personalize Your Trip</h2>
              <p style={{ marginBottom: 16, color: '#666' }}>
                Who is traveling? We&apos;ll tailor attractions, dining, and activities accordingly.
              </p>
              <div className="trip-type-selector">
                {TRIP_TYPES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`trip-type-btn ${tripType === t.id ? 'active' : ''}`}
                    onClick={() => setTripType(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <button className="trip-plan-btn trip-plan-btn-primary" onClick={handleGenerate}>
                <FaRobot /> Generate AI Trip Plan
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TripPlanGeneratePage;
